import { apiFetch } from "@/integrations/backend/client";
import {
  parsePhaseWorkoutsAssistantContent,
  type AIGenerationConversation,
  type PhaseWorkoutPreviewPayload,
} from "@/integrations/backend/program-generation";

export type WorkoutGeneratorLineExercise = {
  mapping_id: string;
  id: string;
  name: string;
  ai_generated: boolean;
};

export type WorkoutGeneratorLine = {
  id: string;
  line_index: number;
  parsed_name: string;
  name_status: "pending" | "confirmed" | "excluded";
  binding_status: "pending" | "finalized" | "skipped";
  exercises: WorkoutGeneratorLineExercise[];
};

export type WorkoutGeneratorBlock = {
  id: string;
  row_index: number;
  block_index: number;
  program_phase_id?: string | null;
  program_phase_title?: string;
  session_structure_name?: string;
  session_block_text: string;
  series_cell_text: string;
  prescription_text: string;
  notes_text: string;
  parse_status: "pending" | "loading" | "complete" | "error" | "skipped";
  parse_error: string;
  lines: WorkoutGeneratorLine[];
};

export type WorkoutGeneratorPhaseSummary = {
  id: string;
  title: string;
  week_start: number;
  week_end: number;
  phase1_start_date: string | null;
  session_structure_id: string | null;
  session_structure_name: string;
  details: string;
};

export type WorkoutGeneratorSessionPayload = {
  session: {
    id: string;
    step: "curation" | "chat";
    conversation_id: string | null;
    approved_exercise_ids: string[];
    phase_details_hash: string;
    stale: boolean;
    week_start?: number;
    week_end?: number;
    phase1_start_date?: string | null;
  };
  blocks: WorkoutGeneratorBlock[];
  phase: {
    id: string;
    title: string;
    week_start: number;
    week_end: number;
    phase1_start_date: string;
    details: string;
  };
  phases?: WorkoutGeneratorPhaseSummary[];
};

export type ExerciseSearchResult = {
  id: string;
  name: string;
};

async function requestJson<T>(path: string, method: string, body?: unknown): Promise<T> {
  const res = await apiFetch(path, {
    method,
    headers: body !== undefined ? { "Content-Type": "application/json" } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `${path}_failed:${res.status}`);
  }
  return (await res.json()) as T;
}

function basePath(profileId: string, phaseId: string) {
  return `/api/v1/admin/profiles/${encodeURIComponent(profileId)}/program/phases/${encodeURIComponent(phaseId)}`;
}

function linePath(profileId: string, phaseId: string, lineId: string) {
  return `${basePath(profileId, phaseId)}/workout-generator/lines/${encodeURIComponent(lineId)}`;
}

export async function getOrCreateWorkoutGeneratorSession(
  profileId: string,
  phaseId: string
): Promise<WorkoutGeneratorSessionPayload> {
  return requestJson<WorkoutGeneratorSessionPayload>(
    `${basePath(profileId, phaseId)}/workout-generator/session`,
    "GET"
  );
}

export async function resetWorkoutGeneratorSession(
  profileId: string,
  phaseId: string
): Promise<WorkoutGeneratorSessionPayload> {
  return requestJson<WorkoutGeneratorSessionPayload>(
    `${basePath(profileId, phaseId)}/workout-generator/session/reset`,
    "POST"
  );
}

export async function parseWorkoutGeneratorBlock(
  profileId: string,
  phaseId: string,
  blockId: string
): Promise<WorkoutGeneratorSessionPayload> {
  return requestJson<WorkoutGeneratorSessionPayload>(
    `${basePath(profileId, phaseId)}/workout-generator/blocks/${encodeURIComponent(blockId)}/parse`,
    "POST"
  );
}

export async function addWorkoutGeneratorBlockLine(
  profileId: string,
  phaseId: string,
  blockId: string,
  parsed_name?: string
): Promise<WorkoutGeneratorSessionPayload> {
  return requestJson<WorkoutGeneratorSessionPayload>(
    `${basePath(profileId, phaseId)}/workout-generator/blocks/${encodeURIComponent(blockId)}/lines`,
    "POST",
    parsed_name ? { parsed_name } : {}
  );
}

export async function updateWorkoutGeneratorLineNameStatus(
  profileId: string,
  phaseId: string,
  lineId: string,
  name_status: "confirmed" | "excluded" | "pending"
): Promise<WorkoutGeneratorSessionPayload> {
  return requestJson<WorkoutGeneratorSessionPayload>(linePath(profileId, phaseId, lineId), "PATCH", {
    name_status,
  });
}

export async function resolveWorkoutGeneratorLine(
  profileId: string,
  phaseId: string,
  lineId: string
): Promise<WorkoutGeneratorSessionPayload> {
  return requestJson<WorkoutGeneratorSessionPayload>(`${linePath(profileId, phaseId, lineId)}/resolve`, "POST");
}

export async function addLineExerciseMapping(
  profileId: string,
  phaseId: string,
  lineId: string,
  exerciseId: string
): Promise<WorkoutGeneratorSessionPayload> {
  return requestJson<WorkoutGeneratorSessionPayload>(`${linePath(profileId, phaseId, lineId)}/mappings`, "POST", {
    exercise_id: exerciseId,
  });
}

export async function removeLineExerciseMapping(
  profileId: string,
  phaseId: string,
  lineId: string,
  mappingId: string
): Promise<WorkoutGeneratorSessionPayload> {
  return requestJson<WorkoutGeneratorSessionPayload>(
    `${linePath(profileId, phaseId, lineId)}/mappings/${encodeURIComponent(mappingId)}`,
    "DELETE"
  );
}

export async function finalizeLineMappings(
  profileId: string,
  phaseId: string,
  lineId: string
): Promise<WorkoutGeneratorSessionPayload> {
  return requestJson<WorkoutGeneratorSessionPayload>(`${linePath(profileId, phaseId, lineId)}/finalize`, "POST");
}

export async function reopenLineMappings(
  profileId: string,
  phaseId: string,
  lineId: string
): Promise<WorkoutGeneratorSessionPayload> {
  return requestJson<WorkoutGeneratorSessionPayload>(`${linePath(profileId, phaseId, lineId)}/reopen`, "POST");
}

export async function aiGenerateWorkoutGeneratorLineExercise(
  profileId: string,
  phaseId: string,
  lineId: string
): Promise<WorkoutGeneratorSessionPayload> {
  return requestJson<WorkoutGeneratorSessionPayload>(
    `${linePath(profileId, phaseId, lineId)}/ai-generate-exercise`,
    "POST"
  );
}

export async function searchExercises(q: string, limit = 500): Promise<ExerciseSearchResult[]> {
  const query = new URLSearchParams({ q, limit: String(limit) });
  const res = await apiFetch(`/api/v1/exercises/search?${query.toString()}`, { method: "GET" });
  if (!res.ok) throw new Error(`exercises_search_failed:${res.status}`);
  return (await res.json()) as ExerciseSearchResult[];
}

export async function generateCuratedPhaseWorkouts(
  profileId: string,
  phaseId: string,
  sessionId: string
): Promise<AIGenerationConversation> {
  return requestJson<AIGenerationConversation>(
    `${basePath(profileId, phaseId)}/workouts/curated/generate`,
    "POST",
    { session_id: sessionId }
  );
}

export async function getCuratedPhaseWorkoutsChat(
  profileId: string,
  phaseId: string,
  conversationId?: string | null
): Promise<AIGenerationConversation> {
  const query = conversationId ? `?conversation_id=${encodeURIComponent(conversationId)}` : "";
  const res = await apiFetch(`${basePath(profileId, phaseId)}/workouts/curated/chat${query}`, {
    method: "GET",
  });
  if (!res.ok) throw new Error(`curated_workouts_chat_get_failed:${res.status}`);
  return (await res.json()) as AIGenerationConversation;
}

export async function sendCuratedPhaseWorkoutsMessage(
  profileId: string,
  phaseId: string,
  input: { conversation_id: string; content: string }
): Promise<AIGenerationConversation> {
  return requestJson<AIGenerationConversation>(
    `${basePath(profileId, phaseId)}/workouts/curated/send`,
    "POST",
    input
  );
}

export async function restartCuratedPhaseWorkouts(
  profileId: string,
  phaseId: string
): Promise<AIGenerationConversation> {
  return requestJson<AIGenerationConversation>(
    `${basePath(profileId, phaseId)}/workouts/curated/restart`,
    "POST"
  );
}

export async function confirmCuratedPhaseWorkouts(
  profileId: string,
  phaseId: string,
  conversationId: string
): Promise<{ created_workout_ids: string[] }> {
  return requestJson<{ created_workout_ids: string[] }>(
    `${basePath(profileId, phaseId)}/workouts/curated/confirm`,
    "POST",
    { conversation_id: conversationId }
  );
}

export function canGenerateWorkout(blocks: WorkoutGeneratorBlock[]): boolean {
  if (blocks.length === 0) return false;
  return blocks.every(
    (block) =>
      (block.parse_status === "complete" || block.parse_status === "skipped") &&
      block.lines.every(
        (line) =>
          line.name_status === "excluded" ||
          (line.name_status === "confirmed" &&
            line.binding_status === "finalized" &&
            line.exercises.length > 0)
      )
  );
}

export function parseCuratedWorkoutPreview(
  conversation: AIGenerationConversation | null
): PhaseWorkoutPreviewPayload | null {
  if (conversation?.parsed_preview && "workouts" in conversation.parsed_preview) {
    return conversation.parsed_preview as PhaseWorkoutPreviewPayload;
  }
  const lastAssistant = (conversation?.messages ?? [])
    .filter((m) => m.role === "assistant")
    .pop();
  return parsePhaseWorkoutsAssistantContent(lastAssistant?.content ?? "");
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export function assistantMessageCount(conversation: AIGenerationConversation | null): number {
  return (conversation?.messages ?? []).filter((m) => m.role === "assistant").length;
}

const GENERATION_FAILED_PREFIX = "Generation failed:";

export function isAssistantGenerationFailure(content: string | null | undefined): boolean {
  return (content ?? "").trim().startsWith(GENERATION_FAILED_PREFIX);
}

export function generationFailureDetail(content: string | null | undefined): string {
  const text = (content ?? "").trim();
  if (text.startsWith(GENERATION_FAILED_PREFIX)) {
    return text.slice(GENERATION_FAILED_PREFIX.length).trim() || "generation failed";
  }
  return text;
}

export function latestAssistantMessage(conversation: AIGenerationConversation | null) {
  return (conversation?.messages ?? []).filter((m) => m.role === "assistant").pop();
}

export async function waitForAssistantResponse({
  conversationId,
  initialAssistantCount,
  fetchConversation,
  applyConversation,
}: {
  conversationId: string | null;
  initialAssistantCount: number;
  fetchConversation: (conversationId: string) => Promise<AIGenerationConversation>;
  applyConversation: (conversation: AIGenerationConversation) => void;
}): Promise<AIGenerationConversation> {
  if (!conversationId) {
    throw new Error("conversation_id is required");
  }
  const timeoutMs = 600_000;
  const intervalMs = 2_000;
  const startedAt = Date.now();
  while (Date.now() - startedAt < timeoutMs) {
    await sleep(intervalMs);
    const next = await fetchConversation(conversationId);
    applyConversation(next);
    if (assistantMessageCount(next) > initialAssistantCount) {
      return next;
    }
  }
  throw new Error("Timed out waiting for AI response");
}
